<template>
  <div class="blik-page">
    <div class="blik-page__wrap">
      <WaitingScreen
        v-if="submitted"
        :has-return-url="!!templateData.has_return_url"
        @return="redirectReturnUrl(true)" />

      <template v-else>
        <h1 class="blik-page__title">{{ $t('blik__title') }}</h1>
        <p class="blik-page__intro">{{ $t('blik__intro') }}</p>

        <div v-if="!hideInvoiceLifetimeCounter" class="blik-page__timer">
          {{ $t('blik__time_remaining') }}:
          <span :class="formatedTime.class" class="blik-page__timer-value">
            {{ formatedTime.time }}
          </span>
        </div>

        <div class="blik-page__field">
          <div class="blik-page__field-label blik-page__field-label--center">
            {{ $t('blik__amount_label') }}
          </div>
          <div class="blik-page__amount">
            <span class="blik-page__amount-value">{{ templateData.amount }}</span>
            <span class="blik-page__amount-currency">{{ templateData.currency }}</span>
            <ButtonCopy :value="`${templateData.amount} ${templateData.currency}`" />
          </div>
        </div>

        <div class="blik-page__field">
          <div class="blik-page__field-label">{{ $t('blik__phone_label') }}</div>
          <div class="blik-page__input">
            <span>{{ templateData.customer_phone }}</span>
            <ButtonCopy :value="templateData.customer_phone" />
          </div>
        </div>

        <div class="blik-page__field">
          <div class="blik-page__field-label">{{ $t('blik__full_name_label') }}</div>
          <div class="blik-page__input">
            <span>{{ templateData.full_name }}</span>
            <ButtonCopy :value="templateData.full_name" />
          </div>
        </div>

        <div class="blik-page__warning">
          {{ $t('blik__warning') }}
        </div>

        <div class="blik-page__steps">
          <div v-for="(step, index) in steps" :key="index" class="blik-page__step">
            <div class="blik-page__step-number">{{ index + 1 }}</div>
            <div class="blik-page__step-label">
              {{ $t(step.key) }}
              <b v-if="step.value">{{ step.value }}</b>
              <span v-if="step.note" class="blik-page__step-note">{{ $t(step.note) }}</span>
            </div>
          </div>
        </div>

        <ul class="blik-page__notes">
          <li>{{ $t('blik__note_1') }}</li>
          <li>{{ $t('blik__note_2') }}</li>
        </ul>

        <div v-if="isExpired" class="blik-page__error">
          {{ $t('blik__expired') }}
        </div>

        <button
          :disabled="loading || isExpired"
          class="blik-page__btn blik-page__btn--primary"
          @click="confirm">
          <div v-if="loading" class="blik-page__btn-loader">
            <Loader />
          </div>
          {{ $t('blik__btn_confirm') }}
        </button>

        <div v-if="isError" class="blik-page__error">
          {{ $t('blik__confirm_error') }}
        </div>
      </template>
    </div>

    <div class="blik-page__footer">
      <div class="blik-page__footer-locale">
        <img src="./assets/locale.svg" alt="" />
        <div
          v-for="(locale, index) in locales"
          :key="index"
          :class="{ active: locale === currentLocale }"
          class="blik-page__footer-locale__item"
          @click="selectLocale(locale)">
          {{ locale.toUpperCase() }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ButtonCopy from './components/ButtonCopy.vue';
import Loader from './components/Loader.vue';
import WaitingScreen from './components/WaitingScreen.vue';
import { createDayJsDurationFormatterFactory } from '@/helpers/DayJsDurationFormatter/DayJsDurationFormatterImpl';
import { messages } from './translations/index';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';

const min30 = 1800000;
const min15 = 900000;
const DEFAULT_LOCALE = 'pl';

export default {
  components: { ButtonCopy, Loader, WaitingScreen },

  created() {
    this.startTimer(this.templateData.expires);

    const confirmPayload = this.flowData?.payload?.confirm_payload;
    this.submitted = confirmPayload !== undefined && confirmPayload !== null;

    if (this.locales.includes(this.selectedLocale)) {
      this.selectLocale(this.selectedLocale);
    } else if (this.locales.includes(DEFAULT_LOCALE)) {
      this.selectLocale(DEFAULT_LOCALE);
    } else {
      this.selectLocale(this.locales[0]);
    }
  },

  data() {
    return {
      loading: false,
      submitted: false,
      isError: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData', 'checkoutOptions']),
    ...mapState('InvoiceLifetime', ['isExpired', 'timeToExpired']),
    ...mapState('UI', ['availableLocales', 'queryLocale', 'layoutConfig']),

    hideInvoiceLifetimeCounter() {
      const config = this.layoutConfig;
      return config &&
        typeof config === 'object' &&
        typeof config.hide_invoice_lifetime_counter === 'boolean'
        ? config.hide_invoice_lifetime_counter
        : false;
    },

    locales() {
      const optionLocales = parseAvailableLocales(
        this.availableLocales,
        this.checkoutOptions?.available_locales,
        Object.keys(messages)
      );

      if (optionLocales.length) {
        return optionLocales;
      }

      return Object.keys(messages);
    },

    selectedLocale() {
      const { default_locale } = this.paymentInvoice;

      const supportedLocale = locale =>
        Object.keys(messages).some(key => key === locale) ? locale : null;

      return (
        supportedLocale(default_locale) ||
        supportedLocale(this.queryLocale) ||
        supportedLocale(this.checkoutOptions?.locale)
      );
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    dateFormatter() {
      return createDayJsDurationFormatterFactory({
        defaultTemplate: 'HH:mm:ss',
      });
    },

    formatedTime() {
      const time = this.dateFormatter().format(this.timeToExpired);

      const classTime =
        this.timeToExpired > min30
          ? null
          : this.timeToExpired <= min30 && this.timeToExpired > min15
          ? 'medium'
          : 'low';

      return {
        time,
        class: classTime,
      };
    },

    steps() {
      const { customer_phone, full_name, amount, currency } = this.templateData;
      return [
        { key: 'blik__step_1' },
        { key: 'blik__step_2' },
        { key: 'blik__step_3' },
        { key: 'blik__step_4', value: customer_phone },
        { key: 'blik__step_5', value: full_name },
        { key: 'blik__step_6', value: `${amount} ${currency}`, note: 'blik__step_6_note' },
        { key: 'blik__step_7' },
      ];
    },

    templateData() {
      const { has_return_url, id, expires } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }
        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }
        return requisites;
      };

      const { customer_phone = '', full_name = '' } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        has_return_url,
        expires,
        customer_phone: customer_phone || '',
        full_name: full_name || '',
        amount: amount != null ? amount.toString() : '',
        currency: currency || '',
      };
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    selectLocale(locale) {
      this.$bus.$emit('changeLocale', locale);
    },

    async confirm() {
      if (this.loading || this.isExpired) return;

      this.isError = false;
      this.loading = true;

      try {
        const { status } = await this.confirmPaymentInvoice({
          id: this.templateData.id,
          data: { payload: { confirmed: true } },
        });

        if (status >= 200 && status < 300) {
          this.submitted = true;
        } else {
          this.isError = true;
        }
      } catch (e) {
        this.isError = true;
      } finally {
        this.loading = false;
      }
    },

    redirectReturnUrl(force) {
      if (!this.templateData.has_return_url) {
        return;
      }

      const isForce = force ? '&force=true' : '';
      location.replace(`/return?id=${this.paymentInvoice.id}${isForce}`);
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

$--blue-color: #0020ff;
$--blue-hover: #334dff;
$--blue-active: #001acc;
$--blue-disabled: #d6daf5;
$--blue-light: #f1f3ff;

$--text-primary: #334150;
$--text-secondary: #7f8fa4;
$--white: #ffffff;

.blik-page {
  display: flex;
  align-items: center;
  flex-direction: column;
  font-family: 'Roboto', sans-serif;

  &__wrap {
    max-width: 540px;
    width: 100%;
    padding: 32px;
    box-shadow: 0px 50px 60px -30px rgba(50, 74, 101, 0.15);
    border-radius: 10px;
    background: $--white;

    @media (max-width: 480px) {
      padding: 16px;
    }
  }

  // Title
  &__title {
    text-align: center;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    color: $--text-primary;
    margin: 0 0 8px;
  }

  // Intro
  &__intro {
    text-align: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $--text-secondary;
    margin: 0 0 20px;
  }

  // Timer
  &__timer {
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: $--text-primary;
    margin-bottom: 20px;

    &-value {
      &.low {
        color: #e82a41;
      }
      &.medium {
        color: #ffbe17;
      }
    }
  }

  &__warning {
    background: #f5b7b1;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 20px;

    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #7f261c;
    text-align: left;
  }

  // Generic field
  &__field {
    margin-bottom: 16px;
  }

  &__field-label {
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: $--text-secondary;
    margin-bottom: 6px;
    text-transform: uppercase;
    text-align: left;

    &--center {
      text-align: center;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: $--blue-light;
    border: 1.5px solid #e1e8ee;
    border-radius: 8px;
    padding: 8px 16px;
    gap: 12px;
    transition: all 0.3s;

    span {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 1.5px;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
    }

    .button-copy {
      flex-shrink: 0;
    }
  }

  // Amount
  &__amount {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: 700;
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0.5px;
    color: $--text-primary;

    &-currency {
      font-weight: 500;
      font-size: 16px;
      color: $--text-secondary;
    }
  }

  // Steps
  &__steps {
    margin: 24px 0 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__step {
    display: grid;
    grid-template-columns: 24px auto;
    gap: 10px;
    align-items: flex-start;

    &-number {
      width: 24px;
      height: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: $--blue-light;
      color: $--blue-color;
      border-radius: 50%;
      font-weight: 700;
      font-size: 11px;
      line-height: 16px;
      flex-shrink: 0;
    }

    &-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;
      color: $--text-primary;
      text-align: left;

      b {
        font-weight: 600;
      }
    }

    &-note {
      display: block;
      font-size: 12px;
      line-height: 16px;
      color: $--text-secondary;
      font-style: italic;
    }
  }

  &__note {
    background: #f8f9fc;
    border-radius: 6px;
    padding: 12px 16px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    color: $--text-secondary;
    margin-bottom: 12px;
  }

  &__notes {
    margin: 0 0 16px;
    padding-left: 18px;

    li {
      font-weight: 400;
      font-size: 13px;
      line-height: 18px;
      color: $--text-secondary;
      list-style-type: disc;
      text-align: left;
    }
  }

  &__error {
    text-align: center;
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #e82a41;
    margin-top: 12px;
  }

  &__btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 340px;
    margin: 0 auto;
    padding: 14px 16px;
    border-radius: 6px;
    border: none;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: background 0.3s, border-color 0.3s, color 0.3s;
    position: relative;
    overflow: hidden;

    &--primary {
      background: $--blue-color;
      color: $--white;
      margin-top: 24px;

      &:hover {
        background: $--blue-hover;
      }

      &:active {
        background: $--blue-active;
      }

      &:disabled {
        background: $--blue-disabled;
        cursor: not-allowed;
      }
    }

    &-loader {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(0, 0, 0, 0.43);
    }
  }

  &__footer {
    max-width: 540px;
    width: 100%;
    margin: 16px 0;
    display: flex;
    justify-content: flex-end;

    &-locale {
      display: flex;
      align-items: center;
      gap: 4px;

      img {
        height: 14px;
        margin-right: 4px;
      }

      &__item {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: $--text-secondary;
        transition: color 0.3s;
        border-right: 1px solid #cdd5db;
        cursor: pointer;
        padding: 0 8px;

        &.active,
        &:hover {
          color: $--blue-color;
        }

        &:last-child {
          border-right: none;
          padding-right: 0;
        }
      }
    }
  }

  @media (max-width: 480px) {
    &__title {
      font-size: 18px;
      line-height: 24px;
      margin-bottom: 4px;
    }

    &__intro {
      font-size: 13px;
      line-height: 18px;
      margin-bottom: 10px;
    }

    &__timer {
      margin-bottom: 10px;
    }

    &__field {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 8px;
    }

    &__field-label {
      flex: 0 0 auto;
      max-width: 45%;
      margin-bottom: 0;
      text-align: left;

      &--center {
        text-align: left;
      }
    }

    &__input {
      flex: 1 1 auto;
      min-width: 0;
      padding: 6px 12px;

      span {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
        overflow-wrap: anywhere;
        letter-spacing: 0.5px;
      }
    }

    &__amount {
      flex: 1 1 auto;
      justify-content: flex-end;
      font-size: 16px;
      line-height: 22px;
    }

    &__warning {
      padding: 8px 10px;
      margin-bottom: 14px;
    }
  }
}
</style>
